import type { ReactNode } from 'react'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'

export type Suffix =
	| 'hourly'
	| 'daily'
	| 'monthly'
	| 'hourlyLong'
	| 'dailyLong'
	| 'monthlyLong'
	| 'person'
	| 'forDelivery'

export type Taxes = 'ht' | 'ttc'

export interface PriceProps {
	price?: number
	suffix?: Suffix
	taxType?: Taxes
	crossed?: boolean
	isMinus?: boolean
	isPlus?: boolean
	className?: string
	taxClassName?: string
	suffixClassName?: string
	suffixComponent?: ReactNode
}

export const Price = ({
	price,
	crossed,
	suffix,
	suffixComponent,
	taxType,
	isMinus,
	isPlus,
	className,
	taxClassName,
	suffixClassName,
}: PriceProps) => {
	const { t, i18n } = useLingui()

	const isFull = Number((price || 0).toString().slice(-2)) // if decimals -> full variant

	if (price === undefined) {
		return (
			<div
				className={cn(
					'relative inline-flex items-end gap-1 font-bold leading-none',
					className,
				)}
			/>
		)
	}

	return (
		<div
			className={cn(
				'relative inline-flex items-end gap-1 font-bold leading-none',
				className,
			)}
		>
			{isMinus && '-'}
			{isPlus && '+ '}

			{i18n.number(price / 100, {
				style: 'currency',
				currency: 'EUR',

				...(!isFull && {
					minimumFractionDigits: 0,
					maximumFractionDigits: 2,
					trailingZeroDisplay: 'auto',
				}),
			})}

			{(taxType || suffix) && (
				<div className='flex items-end leading-none'>
					{taxType && (
						<span
							className={cn('text-xxs font-normal leading-none', taxClassName)}
						>
							{taxType === 'ht' && t`HT`}
							{taxType === 'ttc' && t`TTC`}
						</span>
					)}

					{!suffixComponent && suffix && (
						<span
							className={cn(
								'text-xs font-normal leading-none',
								suffixClassName,
							)}
						>
							{suffix === 'daily' && t`/j`}
							{suffix === 'dailyLong' && t`/jour`}
							{suffix === 'forDelivery' && t`pour la livraison`}
							{suffix === 'hourly' && t`/h`}
							{suffix === 'hourlyLong' && t`/heure`}
							{suffix === 'monthly' && t`/m`}
							{suffix === 'monthlyLong' && t`/mois`}
							{suffix === 'person' && t`/personne`}
						</span>
					)}

					{suffixComponent}
				</div>
			)}

			{crossed && (
				<hr className='absolute left-0 top-1/2 -mt-px w-full border-black' />
			)}
		</div>
	)
}
